import {
  registerApplication,
  start,
  addErrorHandler,
  setBootstrapMaxTime,
  setMountMaxTime,
  getAppStatus,
  LOAD_ERROR,
} from 'single-spa'
import {
  constructApplications,
  constructRoutes,
  constructLayoutEngine,
} from 'single-spa-layout'

import {
  GlobalConfigService,
  GlobalLanguageService,
  GlobalProductToursService,
  GlobalFeatureManagementService,
  GlobalTrackingService,
  GlobalCookieService,
  UsabillaService,
  Platform,
} from '@rmp/services'
import { setupGlobalStyles } from '@rmp/styleguide'
import { AuthClient, UserClient } from '@rmp/auth-client'

import { RoutesConfigManager } from './routes-config'

import { getRedirects } from './routes-config/redirects'
import { getRoutes } from './routes-config/routes'
import {
  TENANT,
  ENVIRONMENT,
  TEAM,
  IS_PRODUCT_TOURS_ENABLED,
  IS_PRODUCTION,
} from './constants'
import logger from './logger'

const BOOTSTRAP_MAX_TIME = 10000

async function startSpa() {
  const routesConfig = new RoutesConfigManager(
    await getRoutes(),
    await getRedirects()
  ).getRoutesConfig()
  const routes = constructRoutes(routesConfig)

  const applications = constructApplications({
    routes,
    loadApp({ name }) {
      return System.import(name)
    },
  })
  constructLayoutEngine({ routes, applications })
  applications.forEach(registerApplication)
  start({
    urlRerouteOnly: true,
  })
}

function initAAExperiment() {
  GlobalFeatureManagementService.partnerHubFeatureManager.getBooleanValue(
    'a_a_experiment'
  )
}

async function initProductTours() {
  if (!IS_PRODUCT_TOURS_ENABLED) return
  await GlobalProductToursService.load()

  const userInfo = UserClient.getUserInfo()
  if (!userInfo || !userInfo.business || !userInfo.business.id) return
  const language = GlobalConfigService.getConfigs()?.lang || 'en';
  const apiUrl = IS_PRODUCTION
    ? 'https://product-tours-api.foodtools.io'
    : 'https://product-tours-api.internal.tkwy-staging.io'
  GlobalProductToursService.init({
    apiUrl,
    userId: userInfo.account.id || '',
    clientId: 'restaurants-portal',
    theme: 'pie-2',
    preferredLanguage: language,
    showTourModalOnNewTours: true,
    attributes: {
      'test-restaurant': userInfo.business.isTestPartner,
      'delivery-by': 'restaurant',
      'establishment-type': userInfo.business.establishmentType,
      'chain-account': userInfo.account.hasMultipleBusinesses,
      country: userInfo.business.country,
      'mobile-viewport': window.innerWidth <= 1024,
    },
  })
}

async function setupPlatform() {
  try {
    const initialLanguage = GlobalLanguageService.getInitialLanguage(TENANT)

    await GlobalConfigService.init({
      platform: Platform.je,
      country: TENANT,
      lang: initialLanguage,
      environment: ENVIRONMENT,
      team: TEAM,
    })
    await AuthClient.init({
      configSubscriber: GlobalConfigService.subscribeToConfigs,
    })
    await GlobalFeatureManagementService.init()
    await GlobalCookieService.init()
    await GlobalTrackingService.init()
    UsabillaService.init()

    setupGlobalStyles()
    initProductTours()

    UserClient.subscribeToUserInfo(() => {
      setupGlobalStyles()
      initProductTours()
    })

    initAAExperiment()
  } catch (error) {
    logger.error(error)
  }
}

async function bootstrapApp() {
  setBootstrapMaxTime(BOOTSTRAP_MAX_TIME)
  setMountMaxTime(BOOTSTRAP_MAX_TIME)

  window.addEventListener('single-spa:first-mount', () => {
    document.querySelector('#root-spinner')?.remove()
    document.querySelector('#root-error-container')?.remove()
  })

  await setupPlatform()
  await startSpa()

  addErrorHandler((error) => {
    // https://single-spa.js.org/docs/api/#handling-load_error-status-to-retry-module
    if (getAppStatus(error.appOrParcelName) === LOAD_ERROR) {
      System.delete(System.resolve(error.appOrParcelName))
    }
    logger.error(error)
  })
}

bootstrapApp()
